/**
 * Reading an image File's natural pixel dimensions in the browser.
 *
 * Extracted verbatim in behaviour from `pages/comms/Messages/Composer.jsx`,
 * where it lived as a private const. Two callers now want it — the chat
 * composer and the broadcast image dropzone — and both want exactly the same
 * contract, so it belongs here rather than being copied.
 */

/** Resolved when the file is not an image, or could not be decoded. */
const UNKNOWN_SIZE = { width: null, height: null };

/**
 * Probe an image File for its natural pixel dimensions.
 *
 * The measurement is done by handing the file to an `Image` through an object
 * URL and reading `naturalWidth`/`naturalHeight` once the decode lands. There
 * is no synchronous way to get this — the bytes have to be decoded first — so
 * the result is a Promise.
 *
 * THE OBJECT URL MUST BE REVOKED ON BOTH PATHS. `URL.createObjectURL` pins the
 * File's bytes in memory for the lifetime of the document, so an un-revoked URL
 * on the error path leaks a whole image per failed probe — and the error path
 * is precisely the one a user retries. Both `onload` and `onerror` revoke
 * before resolving.
 *
 * THIS FUNCTION NEVER REJECTS. Dimension probing is advisory: it decides
 * whether to *warn* about a badly-sized hero image, never whether the upload
 * may proceed. A file the browser cannot decode still uploads fine (the server
 * decodes it, not us), so a decode failure resolves `{width: null, height: null}`
 * and the caller simply skips the advice. A rejection here would let a cosmetic
 * check block a working upload.
 *
 * Deliberate improvement over the original: the original resolved a bare `{}`
 * for the unknown case, which destructures to `undefined`. Explicit `null`s say
 * "measured, unknown" rather than "field absent", which is what callers branch
 * on. Both are falsy, so the existing `...(width ? { width, height } : {})`
 * call site in Composer.jsx is unaffected.
 *
 * @param {File|Blob & { type: string }} file - The picked file.
 * @returns {Promise<{ width: number|null, height: number|null }>} Never rejects.
 */
export const probeImageSize = (file) =>
  new Promise((resolve) => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      return resolve({ ...UNKNOWN_SIZE });
    }

    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      const size = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(size);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ ...UNKNOWN_SIZE });
    };

    img.src = url;
  });

export default probeImageSize;
